/**
 * Base API configuration and utilities
 */
import axios from 'axios';
import { API_BASE, PERFORMANCE_CONFIG } from '../utils/constants';
import { retry, createAbortController } from '../utils/helpers';

// Auth header for raw fetch() calls that bypass this axios client
// (file downloads, EventSource-adjacent code, legacy call sites).
export const authHeaders = () => {
  const token = localStorage.getItem('keycrew-auth-token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Query-param variant for browser-native GETs (<a href> downloads) that
// cannot carry headers. The backend middleware accepts access_token on GET.
export const authTokenParam = () => {
  const token = localStorage.getItem('keycrew-auth-token');
  return token ? `access_token=${encodeURIComponent(token)}` : '';
};

// Create axios instance with default configuration
export const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for loading states and auth
apiClient.interceptors.request.use(
  (config) => {
    // Attach the session token (JWT or legacy shared token)
    const token = localStorage.getItem('keycrew-auth-token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // REMOVED: Timestamp anti-caching that was causing redundant API calls
    // Instead, use proper request deduplication and caching strategies

    // Only add timestamp for specific endpoints that need cache-busting
    const cacheBypassEndpoints = ['/health', '/status'];
    const needsCacheBusting = cacheBypassEndpoints.some(endpoint => 
      config.url && config.url.includes(endpoint)
    );
    
    if (config.method === 'get' && needsCacheBusting) {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // A successful write makes cached reads of the same resource wrong. Done here, on the one
    // path every mutation already goes through, rather than asking 35 call sites to remember.
    // Only on success: a failed write changed nothing, so throwing the cache away would just
    // cost a refetch for no reason.
    const method = (response.config?.method || 'get').toLowerCase();
    if (method !== 'get') invalidateCacheForWrite(response.config?.url);

    // Handle different response formats from the backend
    if (response.data?.success !== undefined) {
      // New modular backend format: { success: true, data: {...}, message: "..." }
      // Error envelopes carry their message under error.message — preserve it
      // so callers can surface the real reason (e.g. "email already exists").
      return {
        ...response,
        data: response.data.data,
        message: response.data.message ?? response.data.error?.message,
        error: response.data.error,
        success: response.data.success,
      };
    }
    
    // Legacy format or direct data
    return response;
  },
  (error) => {
    // Enhanced error handling
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      if (status === 401) {
        console.warn('Authentication required');
        // Only wipe the session for OUR auth 401s (middleware code
        // NOT_AUTHENTICATED). The backend re-raises third-party 401s
        // (expired Shutterstock/KC-OS credentials) — those must not log
        // the user out.
        if (data?.error?.code === 'NOT_AUTHENTICATED') {
          localStorage.removeItem('keycrew-auth-token');
          localStorage.removeItem('keycrew-auth-user');
          if (!window.location.pathname.startsWith('/preview/')) {
            window.location.href = '/';
          }
        }
      } else if (status === 403) {
        // Handle authorization errors
        console.warn('Access denied');
      } else if (status === 429) {
        // Handle rate limiting
        console.warn('Rate limit exceeded');
      } else if (status >= 500) {
        // Handle server errors
        console.error('Server error:', status, data);
      }
      
      // Create consistent error format
      const errorMessage = data?.message || data?.error || `HTTP ${status} Error`;
      error.message = errorMessage;
    } else if (error.request) {
      // Network error
      error.message = 'Network error - please check your connection';
    }
    
    return Promise.reject(error);
  }
);

// API Cache implementation for performance
class APICache {
  constructor(ttl = PERFORMANCE_CONFIG.CACHE_TIMEOUT) {
    this.cache = new Map();
    this.ttl = ttl;
  }
  
  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() - item.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  }
  
  set(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }
  
  clear() {
    this.cache.clear();
  }
  
  delete(key) {
    this.cache.delete(key);
  }
}

export const apiCache = new APICache();

/**
 * Drop cached reads that a write just made stale.
 *
 * WHY THIS EXISTS. The cache had `clear()` and `delete()` from the start and NOTHING EVER CALLED
 * THEM. Every POST/PATCH/DELETE left the matching GET serving pre-write data until the TTL
 * expired, which shows up as the most confusing possible class of bug: you save something, the
 * save succeeds, and the screen keeps showing the old value. Three separate instances of exactly
 * that were fixed by hand — a generated article not appearing in its list, a saved description
 * reverting on reload, a re-attributed quote showing the old attribution — each patched by adding
 * `skipCache: true` to one more read. That is a per-call-site fix for a systemic problem: it only
 * ever protects the reads someone already noticed were wrong, and every new read starts broken.
 *
 * Scoped by RESOURCE ROOT rather than clearing everything, so writing a placement doesn't throw
 * away the interviews list. The root is the first path segment ("/listings", "/content-studio"),
 * which is coarse on purpose: a narrower rule has to model which reads a write can affect, and
 * getting that wrong reintroduces the original bug silently. Over-invalidating costs one refetch;
 * under-invalidating shows someone stale data they will act on.
 */
// Writes whose URL root is NOT the resource they change. `/processing/...` is the whole family:
// seven generation endpoints live under it and every one of them creates articles or flips flags
// on interviews and listings. Root-matching alone would clear `/processing` reads (there are
// none) and leave the article list showing pre-generation data — which is exactly the bug that
// was hand-patched with skipCache when listing generation didn't appear in its list.
//
// Keep this map honest: if a write changes something a different endpoint reads, it belongs here.
const CROSS_RESOURCE_WRITES = {
  processing: ['articles', 'interviews', 'listings', 'content-studio'],
};

function invalidateCacheForWrite(url) {
  const path = String(url || '').split('?')[0];
  const root = path.split('/').filter(Boolean)[0];
  if (!root) {
    // Unrecognisable URL — clear everything rather than guess. An over-eager clear costs a
    // refetch; a missed one shows someone stale data they will act on.
    apiCache.clear();
    return;
  }
  const roots = [root, ...(CROSS_RESOURCE_WRITES[root] || [])];
  for (const key of Array.from(apiCache.cache.keys())) {
    if (roots.some((r) => key.includes(`/${r}`))) apiCache.delete(key);
  }
}

// Enhanced fetch with caching, retry, and abort support
export const fetchWithCache = async (url, options = {}) => {
  const cacheKey = `${url}-${JSON.stringify(options.params || {})}`;
  
  // Check if there's already a pending request for this exact URL
  if (requestQueue.has(cacheKey)) {
    console.log(`🚫 Deduplicating request to: ${url}`);
    return requestQueue.get(cacheKey);
  }
  
  // Check cache first (unless explicitly disabled)
  if (!options.skipCache) {
    const cached = apiCache.get(cacheKey);
    if (cached) {
      console.log(`💾 Serving from cache: ${url}`);
      return { data: cached, fromCache: true };
    }
  }
  
  // Create abort controller for cancellation
  const abortController = createAbortController();
  const config = {
    ...options,
    signal: abortController.signal,
  };
  
  // Create the request promise
  const requestPromise = (async () => {
    try {
      console.log(`🌐 Making API request: ${url}`);
      
      // Retry logic for failed requests
      const response = await retry(
        () => apiClient.get(url, config),
        options.maxRetries || 2,
        options.retryDelay || 1000
      );
      
      // Cache successful GET requests
      if (!options.skipCache && config.method !== 'post') {
        apiCache.set(cacheKey, response.data);
      }
      
      return response;
    } catch (error) {
      // Don't cache errors
      throw error;
    }
  })();
  
  // Add to request queue to prevent duplicates
  return requestQueue.add(cacheKey, requestPromise);
};

// Batch request handler to reduce server load
export class BatchRequestHandler {
  constructor(maxBatchSize = PERFORMANCE_CONFIG.MAX_CONCURRENT_REQUESTS) {
    this.maxBatchSize = maxBatchSize;
    this.requestQueue = [];
    this.processing = false;
  }
  
  async addRequest(requestFn) {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({ requestFn, resolve, reject });
      this.processBatch();
    });
  }
  
  async processBatch() {
    if (this.processing || this.requestQueue.length === 0) return;
    
    this.processing = true;
    
    while (this.requestQueue.length > 0) {
      const batch = this.requestQueue.splice(0, this.maxBatchSize);
      
      try {
        const promises = batch.map(({ requestFn }) => requestFn());
        const results = await Promise.allSettled(promises);
        
        // Resolve/reject individual requests
        results.forEach((result, index) => {
          const { resolve, reject } = batch[index];
          
          if (result.status === 'fulfilled') {
            resolve(result.value);
          } else {
            reject(result.reason);
          }
        });
      } catch (error) {
        // Reject all requests in the batch
        batch.forEach(({ reject }) => reject(error));
      }
      
      // Small delay between batches to prevent overwhelming the server
      if (this.requestQueue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    this.processing = false;
  }
}

export const batchRequestHandler = new BatchRequestHandler();

// Request queue for managing concurrent requests
class RequestQueue {
  constructor() {
    this.requests = new Map();
  }
  
  add(key, promise) {
    this.requests.set(key, promise);
    
    promise
      .finally(() => {
        this.requests.delete(key);
      });
    
    return promise;
  }
  
  get(key) {
    return this.requests.get(key);
  }
  
  has(key) {
    return this.requests.has(key);
  }
  
  clear() {
    this.requests.clear();
  }
}

export const requestQueue = new RequestQueue();

// Utility functions for common API patterns
export const createApiMethod = (method, url, options = {}) => {
  return async (data, config = {}) => {
    const requestConfig = {
      method,
      url,
      ...options,
      ...config,
    };
    
    if (data && ['post', 'put', 'patch'].includes(method.toLowerCase())) {
      requestConfig.data = data;
    } else if (data && method.toLowerCase() === 'get') {
      requestConfig.params = data;
    }
    
    return apiClient(requestConfig);
  };
};

export const createCRUDMethods = (baseUrl) => ({
  getAll: createApiMethod('get', baseUrl),
  getById: (id) => createApiMethod('get', `${baseUrl}/${id}`)(),
  create: createApiMethod('post', baseUrl),
  update: (id, data) => createApiMethod('put', `${baseUrl}/${id}`)(data),
  delete: (id) => createApiMethod('delete', `${baseUrl}/${id}`)(),
});

// Error boundary helper for API calls
export const withErrorBoundary = (apiCall) => {
  return async (...args) => {
    try {
      return await apiCall(...args);
    } catch (error) {
      // Log error for debugging
      console.error('API call failed:', error);
      
      // Re-throw with enhanced error info
      throw {
        ...error,
        timestamp: new Date().toISOString(),
        endpoint: apiCall.name || 'unknown',
      };
    }
  };
};

// Upload progress tracker
export const createUploadConfig = (onProgress) => ({
  onUploadProgress: (progressEvent) => {
    if (onProgress && progressEvent.total) {
      const percentCompleted = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );
      onProgress(percentCompleted);
    }
  },
});

// Cancel token helper
export const createCancelToken = () => {
  const abortController = createAbortController();
  
  return {
    token: abortController.signal,
    cancel: (message = 'Request cancelled') => {
      abortController.abort(message);
    },
  };
};

export default apiClient;